(() => {
  const SELECTED_CLASS = 'loop-widget-purchase-option-selected';
  const HIDDEN_CLASS = 'loop-display-none';
  const MAX_UPDATE_DEPTH = 8;

  class LoopSubscriptionWidgetManual extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.initialize();
    }

    isReploPage() {
      return Boolean(document.querySelector('#replo-fullpage-element'));
    }

    registerWidgetInstance() {
      const formId = this.dataset.productFormId || this.formId || '';
      if (!formId) return true;

      window.__tokuActiveLoopWidgetByForm = window.__tokuActiveLoopWidgetByForm || {};
      const existing = window.__tokuActiveLoopWidgetByForm[formId];
      if (existing && existing !== this) return false;

      window.__tokuActiveLoopWidgetByForm[formId] = this;
      return true;
    }

    initialize() {
      if (this.dataset.initialized === 'true') return;

      this.section = this.closest('[data-section-type="product"]');
      this.supplySelectorMode = this.dataset.supplySelectorMode === 'true';
      this.formId = this.dataset.productFormId;
      if (!this.registerWidgetInstance()) return;

      this.options = Array.from(this.querySelectorAll('[data-loop-widget-purchase-option]'));
      this.subscriptionOptions = this.options.filter((option) => option.dataset.loopOption === 'subscription');
      this.oneTimeOption = this.supplySelectorMode
        ? null
        : this.querySelector('[data-loop-option="one-time"]');
      this.product = this.getProductData();
      this.variant = this.getCurrentVariant();
      this._updateDepth = 0;
      this.preventDuplicateVariantGalleryUpdate();
      this.detachPlanInputsFromExternalForms();

      this.options.forEach((option) => {
        option.addEventListener('click', (event) => {
          if (event.target.closest('[data-loop-plan-select]')) return;
          this.selectOption(option, { userInitiated: true });
        });
        option.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.selectOption(option, { userInitiated: true });
        });
      });

      this.querySelectorAll('[data-loop-plan-select]').forEach((select) => {
        select.addEventListener('change', () => {
          const option = select.closest('[data-loop-option="subscription"]');
          if (!option) return;
          this.checkPlan(option, select.value, option.classList.contains(SELECTED_CLASS));
          this.updateOption(option, this.getVariantForOption(option));
        });
      });

      if (this.section) {
        this.section.addEventListener('theme:variant:change', (event) => {
          if (!event.detail || !event.detail.variant) return;
          if (window.__loopSupplySelectorSyncing) return;
          if (Date.now() < (this._initLockUntil || 0)) return;

          const nextVariantId = String(event.detail.variant.id);
          if (this._lastExternalVariantId === nextVariantId) return;

          clearTimeout(this._externalVariantChangeTimer);
          this._externalVariantChangeTimer = window.setTimeout(() => {
            if (window.__loopSupplySelectorSyncing) return;
            if (this._lastExternalVariantId === nextVariantId) return;

            this._lastExternalVariantId = nextVariantId;
            this.variant = event.detail.variant;
            this.updateForVariant({ fromExternalVariantChange: true });
          }, 32);
        });
      }

      const hasPreselectedSupplyOption =
        this.supplySelectorMode &&
        this.subscriptionOptions.some((option) => option.classList.contains(SELECTED_CLASS));

      if (this.isReploPage()) {
        this._initLockUntil = Date.now() + 1500;
        this._userSelectionLockUntil = Date.now() + 1500;
      }

      if (hasPreselectedSupplyOption) {
        if (this.isReploPage()) {
          this.initializeReploSupplySelection();
        } else {
          this.syncSelectedSupplyOption({ userInitiated: false });
          this.updateForVariant({ fromExternalVariantChange: true });
        }
      } else {
        this.selectDefaultOption({ userInitiated: false });
      }

      this.dataset.initialized = 'true';

      if (this.shouldLogPromoPricing()) {
        console.info(
          '[loop-widget] Promo pricing debug enabled. Disable with window.__TOKU_DEBUG_PROMO_PRICING = false or localStorage.removeItem("toku:debug-promo-pricing")',
        );
        this.logPromoPricing('initialize', {
          mode: this.supplySelectorMode ? 'supply-selector' : 'default',
          promoPercent: this.getPromoDiscountPercent(),
          liquidSupplyDays: Number(this.dataset.supplyDays || 0),
          liquidApplyPromo: this.dataset.applyPromoDiscount === 'true',
          variantId: this.variant?.id,
          variantOptions: this.variant?.options,
          supplyDays: this.getSupplyDays(this.variant),
          shouldApplyPromo: this.shouldApplyPromoDiscount(this.variant),
          liquidRendered: {
            planPrice: this.querySelector('[data-loop-plan-price]')?.textContent?.trim() || null,
            comparePrice: this.querySelector('[data-loop-compare-price]')?.textContent?.trim() || null,
            discountBadge: this.querySelector('[data-loop-discount-badge]')?.textContent?.trim() || null,
          },
        });
      }
    }

    getProductData() {
      const roots = [
        this.section,
        this.closest('[data-section-type="product"]'),
        document.getElementById('replo-fullpage-element'),
        document,
      ].filter(Boolean);

      for (const root of roots) {
        const json = root.querySelector('[data-product-json]');
        if (!json?.textContent?.trim()) continue;

        try {
          return JSON.parse(json.textContent);
        } catch (error) {
          // Try next source.
        }
      }

      const reploProductsEl = document.getElementById('replo-deps-products');
      if (reploProductsEl?.textContent?.trim()) {
        try {
          const entries = JSON.parse(reploProductsEl.textContent);
          const productId = String(this.dataset.productId || '');
          const entry = Array.isArray(entries)
            ? entries.find((candidate) => String(candidate.id) === productId) || entries[0]
            : entries;
          const product = entry?.data || entry;
          if (product?.variants) return product;
        } catch (error) {
          // Ignore malformed Replo product JSON.
        }
      }

      return null;
    }

    getVariantInputs() {
      const inputs = [];
      const seen = new Set();
      const roots = [
        this.section,
        this.closest('[data-section-type="product"]'),
        document.getElementById('replo-fullpage-element'),
        document,
      ].filter(Boolean);
      const selectors = [
        this.formId ? `input[name="id"][form="${this.formId}"]` : null,
        'input[name="id"]',
      ].filter(Boolean);

      roots.forEach((root) => {
        selectors.forEach((selector) => {
          root.querySelectorAll(selector).forEach((input) => {
            if (seen.has(input)) return;
            seen.add(input);
            inputs.push(input);
          });
        });
      });

      if (!this.formId) return inputs;

      const linked = inputs.filter(
        (input) => input.getAttribute('form') === this.formId || input.form?.id === this.formId,
      );
      return linked.length ? linked : inputs;
    }

    getPrimaryVariantInput() {
      const inputs = this.getVariantInputs();
      return inputs[0] || null;
    }

    getCurrentVariant() {
      if (!this.product || !Array.isArray(this.product.variants)) return null;

      const variantInput = this.getPrimaryVariantInput();
      const currentId = variantInput && String(variantInput.value);
      return this.product.variants.find((variant) => String(variant.id) === currentId) || this.product.variants[0];
    }

    formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(cents, window.theme.moneyFormat);
      }

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(Number(cents || 0) / 100);
    }

    getAllocation(variant, planId) {
      if (!variant || !Array.isArray(variant.selling_plan_allocations)) return null;
      return (
        variant.selling_plan_allocations.find((allocation) => {
          const allocationPlanId = allocation.selling_plan_id ?? allocation.selling_plan?.id;
          return String(allocationPlanId) === String(planId);
        }) || null
      );
    }

    getSellingPlan(planId) {
      if (!planId || !this.product?.selling_plan_groups) return null;

      for (const group of this.product.selling_plan_groups) {
        const plan = group.selling_plans?.find((candidate) => String(candidate.id) === String(planId));
        if (plan) return plan;
      }

      return null;
    }

    getSellingPlanAdjustmentPercent(allocation) {
      const planId = allocation?.selling_plan_id;
      const plan = this.getSellingPlan(planId);
      const adjustment = plan?.price_adjustments?.[0];

      if (adjustment?.value_type === 'percentage' && Number(adjustment.value) > 0) {
        return Math.round(Number(adjustment.value));
      }

      return 0;
    }

    hasSellingPlanDiscount(allocation) {
      return this.getSellingPlanAdjustmentPercent(allocation) > 0;
    }

    getPromoBasePrice(allocation, variant) {
      const variantPrice = Number(variant?.price) || 0;
      if (variantPrice > 0) return variantPrice;

      return this.getAllocationPrice(allocation, variant);
    }

    getDisplayComparePrice(allocation, variant, originalPrice, displayPrice, supplyDays = null) {
      if (this.shouldApplyPromoDiscount(variant) && originalPrice > displayPrice) {
        return originalPrice;
      }

      if (this.hasSellingPlanDiscount(allocation)) {
        const compare = Number(allocation?.compare_at_price) || 0;
        if (compare > displayPrice) return compare;

        const variantPrice = Number(variant?.price) || 0;
        if (variantPrice > displayPrice) return variantPrice;
      }

      return 0;
    }

    getDisplaySubscriptionPrice(allocation, variant, supplyDays = null) {
      if (this.shouldApplyPromoDiscount(variant)) {
        return this.applyPromoDiscount(this.getPromoBasePrice(allocation, variant), variant);
      }

      if (this.hasSellingPlanDiscount(allocation)) {
        return this.getAllocationPrice(allocation, variant);
      }

      const days = supplyDays ?? this.getSupplyDays(variant);
      if (days < 90) {
        const variantPrice = Number(variant?.price) || 0;
        if (variantPrice > 0) return variantPrice;
      }

      return this.getAllocationPrice(allocation, variant);
    }

    getSupplyDays(variant) {
      if (!variant || !Array.isArray(variant.options)) return 0;

      for (const option of variant.options) {
        const match = String(option).match(/(\d+)\s*day/i);
        if (match) return Number(match[1]);
      }

      return 0;
    }

    getPromoDiscountPercent() {
      const percent = Number.parseInt(this.dataset.promoDiscountPercent || '30', 10);
      return Number.isFinite(percent) && percent > 0 ? percent : 0;
    }

    shouldApplyPromoDiscount(variant) {
      return this.getSupplyDays(variant) >= 90 && this.getPromoDiscountPercent() > 0;
    }

    getAllocationPrice(allocation, variant) {
      const allocationPrice = Number(allocation?.price);
      if (Number.isFinite(allocationPrice) && allocationPrice > 0) {
        return allocationPrice;
      }

      const perDeliveryPrice = Number(allocation?.per_delivery_price);
      if (Number.isFinite(perDeliveryPrice) && perDeliveryPrice > 0) {
        return perDeliveryPrice;
      }

      return 0;
    }

    shouldLogPromoPricing() {
      return window.__TOKU_DEBUG_PROMO_PRICING !== false;
    }

    logPromoPricing(context, payload) {
      if (!this.shouldLogPromoPricing()) return;

      console.groupCollapsed(`[loop-widget] promo pricing: ${context}`);
      console.log(payload);
      console.groupEnd();
    }

    applyPromoDiscount(cents, variant) {
      const amount = Number(cents) || 0;
      if (!this.shouldApplyPromoDiscount(variant)) return amount;

      const remaining = 100 - this.getPromoDiscountPercent();
      return Math.round((amount * remaining) / 100);
    }

    getDaysOptionIndex() {
      if (!this.product || !Array.isArray(this.product.options)) return -1;
      return this.product.options.findIndex((name) => /day|supply/i.test(String(name)));
    }

    getFlavorOptionIndex() {
      if (!this.product || !Array.isArray(this.product.options)) return -1;
      return this.product.options.findIndex((name) => /flavor|flavour/i.test(String(name)));
    }

    isYourBundleSelected() {
      const flavorWrapper =
        this.section?.querySelector('.selector-wrapper[data-option-handle="pick-flavor"]') ||
        document.querySelector('.selector-wrapper[data-option-handle="pick-flavor"]');
      const checked = flavorWrapper?.querySelector('input[type="radio"][data-single-option-selector]:checked');
      return Boolean(checked && checked.value === 'bundle');
    }

    getBundlePackValue() {
      const bundleForm =
        this.section?.querySelector('bundle-form') || document.querySelector('bundle-form');
      if (!bundleForm || typeof bundleForm.getStateParts !== 'function') return '';
      if (typeof bundleForm.statePartsToPackValue !== 'function') return '';

      const stateParts = bundleForm.getStateParts();
      if (stateParts.length === 1 && Number(stateParts[0]?.qty) === 3) {
        if (typeof bundleForm.getSingleFlavorValue === 'function') {
          return bundleForm.getSingleFlavorValue(stateParts) || '';
        }
      }

      return bundleForm.statePartsToPackValue(stateParts) || '';
    }

    findVariantByFlavorPack(packValue, targetDays) {
      if (!packValue || !this.product?.variants) return null;

      const flavorIndex = this.getFlavorOptionIndex();
      if (flavorIndex < 0) return null;

      return (
        this.product.variants.find(
          (variant) =>
            this.getSupplyDays(variant) === targetDays && variant.options?.[flavorIndex] === packValue,
        ) || null
      );
    }

    resolveBundleVariantForSupplyDays(supplyDays) {
      const packValue = this.getBundlePackValue();
      if (!packValue) return this.getCurrentVariant();

      return this.findVariantByFlavorPack(packValue, supplyDays) || null;
    }

    getDaysWrapper() {
      const wrappers = this.section?.querySelectorAll('.selector-wrapper[data-option-handle]');
      if (!wrappers) return null;

      return (
        Array.from(wrappers).find((wrapper) => /day|supply/i.test(wrapper.dataset.optionHandle || '')) || null
      );
    }

    getSupplyValueForDays(targetDays) {
      if (targetDays === 90) return this.dataset.daysValue90 || '';
      if (targetDays === 30) return this.dataset.daysValue30 || '';
      return '';
    }

    getBaseVariant() {
      if (this.variant) return this.variant;
      return this.getCurrentVariant();
    }

    variantMatchesCurrentOptions(variant, baseVariant, daysIndex = this.getDaysOptionIndex()) {
      if (!variant || !baseVariant || !Array.isArray(variant.options) || !Array.isArray(baseVariant.options)) {
        return false;
      }

      return variant.options.every((opt, index) => {
        const optionName = String(this.product?.options?.[index] || '').toLowerCase();
        if (index === daysIndex || /day|supply/.test(optionName)) return true;
        return opt === baseVariant.options[index];
      });
    }

    findVariantBySupplyDays(targetDays) {
      if (!this.product?.variants) return null;

      const baseVariant = this.getBaseVariant();
      const daysIndex = this.getDaysOptionIndex();

      const matchWithCurrentOptions = (variant) => {
        if (this.getSupplyDays(variant) !== targetDays) return false;
        if (!baseVariant) return true;
        return this.variantMatchesCurrentOptions(variant, baseVariant, daysIndex);
      };

      const matched = this.product.variants.find(matchWithCurrentOptions);
      if (matched) return matched;

      return null;
    }

    getVariantForOption(option) {
      if (!this.supplySelectorMode) return this.variant;

      const supplyDays = Number(option.dataset.loopSupplyDays);
      if (!supplyDays) return this.variant;

      if (this.isYourBundleSelected()) {
        const bundleVariant = this.resolveBundleVariantForSupplyDays(supplyDays);
        if (bundleVariant) return bundleVariant;
      }

      if (!this.product?.variants) {
        const variantId = option?.dataset?.variantId;
        return variantId ? { id: Number(variantId) } : this.variant;
      }

      const variantByDays = this.findVariantBySupplyDays(supplyDays);
      if (variantByDays) return variantByDays;

      const variantId = option?.dataset?.variantId;
      if (variantId) {
        const variantById = this.product.variants.find((variant) => String(variant.id) === String(variantId));
        const baseVariant = this.getBaseVariant();
        if (
          variantById &&
          this.getSupplyDays(variantById) === supplyDays &&
          (!baseVariant || this.variantMatchesCurrentOptions(variantById, baseVariant))
        ) {
          return variantById;
        }
      }

      return null;
    }

    getCartForms() {
      const forms = [];
      const seen = new Set();
      const add = (form) => {
        if (form instanceof HTMLFormElement && !seen.has(form)) {
          seen.add(form);
          forms.push(form);
        }
      };

      if (this.formId) add(document.getElementById(this.formId));

      const productId = this.dataset.productId;
      if (productId) add(document.getElementById(`product-form-${productId}`));

      const reploRoot = document.getElementById('replo-fullpage-element');
      if (reploRoot) reploRoot.querySelectorAll('form').forEach(add);

      this.section?.querySelectorAll('form.product-form, product-form form').forEach(add);

      return forms;
    }

    detachPlanInputsFromExternalForms() {
      if (!this.supplySelectorMode) return;
      this.querySelectorAll('[data-loop-plan-input]').forEach((input) => {
        input.removeAttribute('form');
      });
    }

    setHiddenFormField(form, name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      input.dataset.tokuCartField = 'true';
      form.appendChild(input);
      return input;
    }

    clearSellingPlanFields(form) {
      if (!form) return;

      form.querySelectorAll('[name="selling_plan"]').forEach((node) => node.remove());

      if (form.id) {
        document.querySelectorAll(`[name="selling_plan"][form="${CSS.escape(form.id)}"]`).forEach((node) => {
          node.checked = false;
        });
      }
    }

    syncSellingPlanToForms(planId) {
      this.getCartForms().forEach((form) => {
        this.clearSellingPlanFields(form);
        if (!planId) return;
        this.setHiddenFormField(form, 'selling_plan', planId);
      });
    }

    getSelectedCartPayload() {
      if (!this.supplySelectorMode) return null;

      const selectedOption = this.subscriptionOptions.find((option) => option.classList.contains(SELECTED_CLASS));
      if (!selectedOption) return null;

      const variant = this.getVariantForOption(selectedOption);
      if (!variant) return null;

      const planInputs = Array.from(selectedOption.querySelectorAll('[data-loop-plan-input]'));
      const supplyDays = Number(selectedOption.dataset.loopSupplyDays) || this.getSupplyDays(variant);
      const { planId } = this.findBestAllocation(variant, planInputs, supplyDays);

      return {
        variantId: variant.id,
        planId,
      };
    }

    ensureCartFormState(form) {
      if (!this.supplySelectorMode || !form) return;

      const payload = this.getSelectedCartPayload();
      if (!payload?.variantId) return;

      if (form.id) {
        document.querySelectorAll(`[name="id"][form="${CSS.escape(form.id)}"]`).forEach((input) => {
          input.value = String(payload.variantId);
        });
      }

      const inlineId = form.querySelector('[name="id"]');
      if (inlineId) {
        inlineId.value = String(payload.variantId);
      } else {
        this.setHiddenFormField(form, 'id', payload.variantId);
      }

      this.clearSellingPlanFields(form);
      if (payload.planId) {
        this.setHiddenFormField(form, 'selling_plan', payload.planId);
      }

      const selectedOption = this.subscriptionOptions.find((option) => option.classList.contains(SELECTED_CLASS));
      if (selectedOption && payload.planId) {
        selectedOption.querySelectorAll('[data-loop-plan-input]').forEach((input) => {
          input.checked = String(input.value) === String(payload.planId);
        });
      }

      this.variant = this.product?.variants?.find((variant) => String(variant.id) === String(payload.variantId)) || this.variant;
    }

    setVariantId(variantId, options = {}) {
      const dispatchChange = options.dispatchChange !== false;
      const variantInputs = this.getVariantInputs();
      if (!variantInputs.length) return false;

      let changed = false;
      variantInputs.forEach((variantInput) => {
        if (String(variantInput.value) === String(variantId)) return;
        variantInput.value = String(variantId);
        changed = true;
      });

      if (this.product) {
        this.variant = this.product.variants.find((variant) => String(variant.id) === String(variantId)) || this.variant;
      }

      if (changed && dispatchChange) {
        variantInputs[0].dispatchEvent(new Event('change', { bubbles: true }));
        variantInputs[0].dispatchEvent(new Event('input', { bubbles: true }));
      }

      return true;
    }

    findSupplyInput(wrapper, targetDays) {
      if (!wrapper) return null;

      const supplyValue = this.getSupplyValueForDays(targetDays);
      if (supplyValue) {
        const exactMatch = wrapper.querySelector(`input[data-single-option-selector][value="${CSS.escape(supplyValue)}"]`);
        if (exactMatch) return exactMatch;
      }

      return (
        Array.from(wrapper.querySelectorAll('input[data-single-option-selector]')).find((input) => {
          const match = String(input.value).match(/(\d+)\s*day/i);
          return match && Number(match[1]) === targetDays;
        }) || null
      );
    }

    syncCartFormsFromSelection() {
      this.getCartForms().forEach((form) => {
        this.ensureCartFormState(form);
      });
    }

    setSupplyDays(targetDays, options = {}) {
      const userInitiated = options.userInitiated === true;
      let variant = options.variant || this.findVariantBySupplyDays(targetDays);
      if (variant && this.getSupplyDays(variant) !== targetDays) {
        variant = this.findVariantBySupplyDays(targetDays);
      }
      if (!variant || this.getSupplyDays(variant) !== targetDays) return false;

      const currentDays = this.getSupplyDays(this.variant);
      const variantInputs = this.getVariantInputs();
      const alreadyOnVariant = variantInputs.some((input) => String(input.value) === String(variant.id));

      if (this.isReploPage()) {
        if (alreadyOnVariant && currentDays === targetDays) {
          this.syncCartFormsFromSelection();
          return true;
        }

        window.__loopSupplySelectorSyncing = true;
        this.variant = variant;
        this._lastExternalVariantId = String(variant.id);
        this.setVariantId(variant.id, { dispatchChange: false });
        this.syncCartFormsFromSelection();
        window.setTimeout(() => {
          window.__loopSupplySelectorSyncing = false;
        }, 64);
        return true;
      }

      const wrapper = this.getDaysWrapper();
      const input = this.findSupplyInput(wrapper, targetDays);
      const alreadyOnDays = input && input.checked;

      if (alreadyOnVariant && alreadyOnDays && currentDays === targetDays) {
        return true;
      }

      window.__loopSupplySelectorSyncing = true;

      if (input && !input.checked) {
        input.checked = true;
        if (userInitiated) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.setVariantId(variant.id, { dispatchChange: userInitiated });

      window.setTimeout(() => {
        window.__loopSupplySelectorSyncing = false;
      }, 64);

      return true;
    }

    updateFeatureThreeText(days) {
      const use90DayText = days >= 90;
      const section = this.closest('[data-section-type="product"]') || this.closest('[data-product]') || this;
      const roots = section === this ? [this] : [this, section];

      roots.forEach((root) => {
        root.querySelectorAll('[data-loop-feature-three-text], [data-money-back-guarantee]').forEach((container) => {
          const text30 = container.querySelector('[data-loop-supply-text="30"]');
          const text90 = container.querySelector('[data-loop-supply-text="90"]');
          if (text30) text30.hidden = use90DayText;
          if (text90) text90.hidden = !use90DayText;
        });
      });
    }

    getDiscountText(allocation, variant, supplyDays = null) {
      const suffix = this.dataset.badgeSuffix || '';
      const days = supplyDays ?? this.getSupplyDays(variant);

      if (this.shouldApplyPromoDiscount(variant)) {
        return `Save ${this.getPromoDiscountPercent()}%${suffix ? ` ${suffix}` : ''}`;
      }

      const planPercent = this.getSellingPlanAdjustmentPercent(allocation);
      if (planPercent > 0) {
        if (days >= 90 && suffix) {
          return `Save ${planPercent}% ${suffix}`;
        }
        return `Save ${planPercent}%`;
      }

      return days >= 90 ? suffix || '' : '';
    }

    getDiscountTextShort(allocation, variant, supplyDays = null) {
      if (variant && this.shouldApplyPromoDiscount(variant)) {
        return `Save ${this.getPromoDiscountPercent()}%`;
      }

      const planPercent = this.getSellingPlanAdjustmentPercent(allocation);
      return planPercent > 0 ? `Save ${planPercent}%` : '';
    }

    getSubscriptionLabel(option) {
      if (!this.supplySelectorMode) {
        return this.dataset.subscriptionLabel || 'Subscribe';
      }

      return Number(option?.dataset.loopSupplyDays) === 30
        ? this.dataset.supply30Label || '30-Day Supply'
        : this.dataset.supply90Label || '90-Day Supply';
    }

    getUnitText(supplyDays) {
      if (supplyDays === 30) return '/Serving';
      if (supplyDays > 0) return ' / Day';
      return ' per delivery';
    }

    getMonthlyUnitPrice(displayPrice, supplyDays) {
      if (supplyDays >= 90) return Math.round(displayPrice / 3);
      return displayPrice;
    }

    getFullComparePrice(displayCompare, displayPrice, variant) {
      const variantPrice = variant?.price || 0;
      return displayCompare > displayPrice ? displayCompare : variantPrice;
    }

    getServingPrice(displayPrice, supplyDays) {
      if (supplyDays > 0) return Math.floor(displayPrice / supplyDays);
      return displayPrice;
    }

    findBestAllocation(variant, planInputs, supplyDays = null) {
      if (!variant?.selling_plan_allocations?.length) {
        return { allocation: null, planId: null };
      }

      const allocations = variant.selling_plan_allocations;
      const preferredPlanIds = planInputs.map((input) => String(input.value)).filter(Boolean);

      for (const planId of preferredPlanIds) {
        const allocation = this.getAllocation(variant, planId);
        if (allocation) return { allocation, planId };
      }

      for (const allocation of allocations) {
        const planId = allocation.selling_plan_id;
        if (preferredPlanIds.includes(String(planId))) {
          return { allocation, planId };
        }
      }

      if (allocations.length === 1) {
        return { allocation: allocations[0], planId: allocations[0].selling_plan_id };
      }

      const days =
        supplyDays ??
        (Number(planInputs[0]?.closest('[data-loop-widget-purchase-option]')?.dataset?.loopSupplyDays) ||
          this.getSupplyDays(variant));

      if (days) {
        const matched = allocations.find((allocation) => {
          const plan = this.getSellingPlan(allocation.selling_plan_id);
          if (!plan) return false;
          const planName = String(plan.name || '').toLowerCase();
          if (days >= 90) {
            return /90|quarter|3 month|every 3/.test(planName);
          }
          return /(^30|\bmonthly\b|every month|every 1 month)/.test(planName);
        });

        if (matched) {
          return { allocation: matched, planId: matched.selling_plan_id };
        }
      }

      return { allocation: null, planId: null };
    }

    setSelectedOption(selectedOption) {
      this.options.forEach((option) => {
        const selected = option === selectedOption;
        option.classList.toggle(SELECTED_CLASS, selected);
        option.setAttribute('aria-checked', selected ? 'true' : 'false');
        option.setAttribute('tabindex', selected ? '0' : '-1');

        const groupControl = option.querySelector('[data-loop-group-control]');
        if (groupControl) groupControl.checked = selected;
      });
    }

    checkPlan(option, planId, dispatchChange) {
      let selectedInput = null;
      option.querySelectorAll('[data-loop-plan-input]').forEach((input) => {
        input.checked = String(input.value) === String(planId);
        if (input.checked) selectedInput = input;
      });

      const select = option.querySelector('[data-loop-plan-select]');
      if (select && selectedInput) select.value = String(selectedInput.value);

      if (planId) {
        this.syncSellingPlanToForms(planId);
      }

      if (dispatchChange && selectedInput) {
        this.dispatchPlanChange(selectedInput);
      }

      return selectedInput;
    }

    getProductForms() {
      return this.section ? Array.from(this.section.querySelectorAll('product-form')) : [];
    }

    preventDuplicateVariantGalleryUpdate() {
      if (!window.use_media_images) return;

      this.getProductForms().forEach((productForm) => {
        if (
          productForm.dataset.loopGalleryGuard === 'true' ||
          typeof productForm.updateImageGallery !== 'function' ||
          typeof productForm.updateProductImage !== 'function'
        ) {
          return;
        }

        productForm.updateProductImage = () => {};
        productForm.dataset.loopGalleryGuard = 'true';
      });
    }

    dispatchPlanChange(input) {
      const methods = ['updateProductImage', 'updateImageGallery'];
      const mediaUpdates = [];

      this.getProductForms().forEach((productForm) => {
        methods.forEach((method) => {
          if (typeof productForm[method] !== 'function') return;

          mediaUpdates.push({
            productForm,
            method,
            callback: productForm[method],
          });
          productForm[method] = () => {};
        });
      });

      try {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        mediaUpdates.forEach(({ productForm, method, callback }) => {
          productForm[method] = callback;
        });
      }
    }

    uncheckPlans(dispatchChange = true) {
      let changeTarget = null;
      this.querySelectorAll('[data-loop-plan-input]').forEach((input) => {
        if (input.checked) changeTarget = input;
        input.checked = false;
      });

      this.syncSellingPlanToForms(null);

      changeTarget = changeTarget || this.querySelector('[data-loop-plan-input]');
      if (changeTarget && dispatchChange) this.dispatchPlanChange(changeTarget);
    }

    selectDefaultOption(options = {}) {
      const userInitiated = options.userInitiated === true;

      if (this.supplySelectorMode) {
        const defaultDays = Number(this.dataset.defaultSupplyDays || 90);
        const availableOption =
          this.subscriptionOptions.find((option) => Number(option.dataset.loopSupplyDays) === defaultDays && !option.hidden) ||
          this.subscriptionOptions.find((option) => !option.hidden);
        if (availableOption) this.selectOption(availableOption, { userInitiated });
        return;
      }

      if (this.dataset.defaultPurchaseOption === 'one_time') {
        this.selectOption(this.oneTimeOption, { userInitiated });
        return;
      }

      const availableSubscription = this.subscriptionOptions.find((option) => !option.hidden);
      this.selectOption(availableSubscription || this.oneTimeOption, { userInitiated });
    }

    syncSelectedSupplyOption(options = {}) {
      const userInitiated = options.userInitiated === true;
      const selectedOption = this.subscriptionOptions.find((option) => option.classList.contains(SELECTED_CLASS));
      if (!selectedOption) return;

      this.setSelectedOption(selectedOption);

      const supplyDays = Number(selectedOption.dataset.loopSupplyDays);
      if (supplyDays) {
        this.setSupplyDays(supplyDays, {
          userInitiated,
          variant: this.getVariantForOption(selectedOption),
        });
      }

      const planInputs = Array.from(selectedOption.querySelectorAll('[data-loop-plan-input]'));
      const { planId } = this.findBestAllocation(this.getVariantForOption(selectedOption), planInputs, Number(selectedOption.dataset.loopSupplyDays));
      if (planId) this.checkPlan(selectedOption, planId, userInitiated);
    }

    initializeReploSupplySelection() {
      const selectedOption = this.subscriptionOptions.find((option) => option.classList.contains(SELECTED_CLASS));
      if (!selectedOption) {
        this.selectDefaultOption({ userInitiated: false });
        return;
      }

      const variantForOption = this.getVariantForOption(selectedOption);
      if (!variantForOption) {
        this.updateForVariant({ skipSelectionSync: true });
        return;
      }

      this.variant = variantForOption;
      this._lastExternalVariantId = String(variantForOption.id);
      this._userSelectionLockUntil = Date.now() + 1500;

      const primaryInput = this.getPrimaryVariantInput();
      const needsVariantSync = primaryInput && String(primaryInput.value) !== String(variantForOption.id);

      window.__loopSupplySelectorSyncing = true;

      if (needsVariantSync) {
        this.setVariantId(variantForOption.id, { dispatchChange: false });
      }

      this.subscriptionOptions.forEach((otherOption) => {
        if (otherOption === selectedOption) return;
        otherOption.querySelectorAll('[data-loop-plan-input]').forEach((input) => {
          input.checked = false;
        });
      });

      const planInputs = Array.from(selectedOption.querySelectorAll('[data-loop-plan-input]'));
      const { planId } = this.findBestAllocation(
        variantForOption,
        planInputs,
        Number(selectedOption.dataset.loopSupplyDays),
      );
      if (planId) this.checkPlan(selectedOption, planId, false);

      this.syncCartFormsFromSelection();

      window.setTimeout(() => {
        window.__loopSupplySelectorSyncing = false;
      }, 100);

      this.updateForVariant({ skipSelectionSync: true });
    }

    getPurchaseButtonText() {
      if (this.supplySelectorMode) {
        return this.dataset.subscriptionButtonText || 'Subscribe & Save';
      }

      const subscriptionSelected = this.subscriptionOptions.some(
        (option) => !option.hidden && option.classList.contains(SELECTED_CLASS)
      );

      if (subscriptionSelected) {
        return this.dataset.subscriptionButtonText || 'Subscribe & Save';
      }

      return this.dataset.oneTimeButtonText || 'Buy Now';
    }

    updateButtonText() {
      if (this.variant && this.variant.available === false) return;

      const buttonText = this.section && this.section.querySelector('[data-add-to-cart-text]');
      if (buttonText) buttonText.textContent = this.getPurchaseButtonText();
    }

    getSelectedPriceCents() {
      if (!this.variant) return { price: 0, compare: 0 };

      const subscriptionSelected = this.subscriptionOptions.some(
        (option) => !option.hidden && option.classList.contains(SELECTED_CLASS)
      );

      if (subscriptionSelected) {
        const selectedOption = this.subscriptionOptions.find(
          (option) => !option.hidden && option.classList.contains(SELECTED_CLASS)
        );

        if (selectedOption) {
          const variantForOption = this.getVariantForOption(selectedOption);
          const supplyDays = Number(selectedOption.dataset.loopSupplyDays) || this.getSupplyDays(variantForOption);
          const planInputs = Array.from(selectedOption.querySelectorAll('[data-loop-plan-input]'));
          const { allocation } = this.findBestAllocation(variantForOption, planInputs);

          if (allocation) {
            const displayPrice = this.getDisplaySubscriptionPrice(allocation, variantForOption, supplyDays);
            const applyPromo = this.shouldApplyPromoDiscount(variantForOption);
            const promoBasePrice = applyPromo
              ? this.getPromoBasePrice(allocation, variantForOption)
              : this.getAllocationPrice(allocation, variantForOption);
            const compare = this.getDisplayComparePrice(
              allocation,
              variantForOption,
              promoBasePrice,
              displayPrice,
              supplyDays,
            );

            return {
              price: displayPrice,
              compare,
            };
          }
        }
      }

      const originalPrice = this.variant.price;

      return {
        price: originalPrice,
        compare: this.variant.compare_at_price || 0,
      };
    }

    updateButtonPrice() {
      const priceWrapper = this.section && this.section.querySelector('[data-add-to-cart-price]');
      if (!priceWrapper) return;

      const currentEl = priceWrapper.querySelector('[data-add-to-cart-current-price]');
      const compareEl = priceWrapper.querySelector('[data-add-to-cart-compare-price]');
      if (!currentEl) return;

      if (!this.variant || this.variant.available === false) {
        currentEl.textContent = '';
        if (compareEl) {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
        return;
      }

      const { price, compare } = this.getSelectedPriceCents();
      const showCompare = compare > price;

      currentEl.textContent = this.formatMoney(price);
      if (compareEl) {
        compareEl.textContent = showCompare ? this.formatMoney(compare) : '';
        compareEl.hidden = !showCompare;
      }
    }

    scheduleButtonTextUpdate() {
      clearTimeout(this._buttonTextTimer);
      this._buttonTextTimer = window.setTimeout(() => {
        this.updateButtonText();
        this.updateButtonPrice();
      }, 48);
    }

    selectOption(option, options = {}) {
      if (!option || option.hidden) return;

      const userInitiated = options.userInitiated === true;
      if (userInitiated) {
        this._userSelectionLockUntil = Date.now() + (this.isReploPage() ? 2000 : 800);
      }

      this.setSelectedOption(option);

      this.subscriptionOptions.forEach((otherOption) => {
        if (otherOption === option) return;
        otherOption.querySelectorAll('[data-loop-plan-input]').forEach((input) => {
          input.checked = false;
        });
      });

      const variantForOption = this.getVariantForOption(option);
      if (!variantForOption) return;

      if (userInitiated) {
        this._lastExternalVariantId = String(variantForOption.id);
      }

      if (this.supplySelectorMode) {
        const supplyDays = Number(option.dataset.loopSupplyDays);
        if (supplyDays) this.setSupplyDays(supplyDays, { userInitiated, variant: variantForOption });
        else this.setVariantId(variantForOption.id, { dispatchChange: userInitiated });
      } else if (option === this.oneTimeOption) {
        this.uncheckPlans(userInitiated);
        this.scheduleButtonTextUpdate();
        return;
      }

      const planInputs = Array.from(option.querySelectorAll('[data-loop-plan-input]'));
      const supplyDays = Number(option.dataset.loopSupplyDays) || this.getSupplyDays(variantForOption);
      const { planId } = this.findBestAllocation(variantForOption, planInputs, supplyDays);
      const dispatchPlanChange = userInitiated && !this.isReploPage();
      const input = planId ? this.checkPlan(option, planId, dispatchPlanChange) : null;

      if (!input && this.supplySelectorMode && planId) {
        this.syncSellingPlanToForms(planId);
      }

      if (this.supplySelectorMode && this.isReploPage()) {
        this.syncCartFormsFromSelection();
      }

      if (!input && !this.supplySelectorMode) {
        this.setSelectedOption(this.oneTimeOption);
        this.uncheckPlans(userInitiated);
      }

      if (this.supplySelectorMode) {
        this.variant = variantForOption;
      }

      this.updateForVariant({ skipSelectionSync: true });
      this.scheduleButtonTextUpdate();
    }

    updateOption(option, variant) {
      const planInputs = Array.from(option.querySelectorAll('[data-loop-plan-input]'));
      const select = option.querySelector('[data-loop-plan-select]');
      const supplyDays = Number(option.dataset.loopSupplyDays) || this.getSupplyDays(variant);
      let { allocation, planId: selectedPlanId } = this.findBestAllocation(variant, planInputs, supplyDays);

      if (this.supplySelectorMode) {
        option.hidden = false;

        if (!variant) return false;

        if (!allocation) {
          allocation = {
            price: variant.price,
            compare_at_price: 0,
            selling_plan_id: selectedPlanId || null,
          };
        }

        if (option.classList.contains(SELECTED_CLASS) && selectedPlanId) {
          this.checkPlan(option, selectedPlanId, false);
        } else if (select && selectedPlanId) {
          select.value = String(selectedPlanId);
        }
      } else {
        let selectedPlanIdFromControl = select ? select.value : planInputs.find((input) => input.checked)?.value;
        allocation = this.getAllocation(variant, selectedPlanIdFromControl);

        if (!allocation) {
          const firstAvailableInput = planInputs.find((input) => this.getAllocation(variant, input.value));
          if (firstAvailableInput) {
            selectedPlanId = firstAvailableInput.value;
            allocation = this.getAllocation(variant, selectedPlanId);
            if (option.classList.contains(SELECTED_CLASS)) {
              this.checkPlan(option, selectedPlanId, true);
            } else if (select) {
              select.value = String(selectedPlanId);
            }
          }
        }

        option.hidden = !allocation;
        if (!allocation) return false;
      }

      const applyPromo = this.shouldApplyPromoDiscount(variant);
      const allocationPrice = this.getAllocationPrice(allocation, variant);
      const promoBasePrice = applyPromo ? this.getPromoBasePrice(allocation, variant) : allocationPrice;
      const displayPrice = this.getDisplaySubscriptionPrice(allocation, variant, supplyDays);
      const displayCompare = this.getDisplayComparePrice(
        allocation,
        variant,
        promoBasePrice,
        displayPrice,
        supplyDays,
      );

      const unitPrice = supplyDays > 0 ? Math.floor(displayPrice / supplyDays) : allocation.per_delivery_price || displayPrice;
      const label = option.querySelector('[data-loop-subscription-option-label]');
      const unitPriceEl = option.querySelector('[data-loop-unit-price]');
      const unitTextEl = option.querySelector('[data-loop-unit-text]');
      const unitPriceMonthlyEl = option.querySelector('[data-loop-unit-price-monthly]');
      const unitCompareMonthlyEl = option.querySelector('[data-loop-unit-compare-monthly]');
      const unitTextMonthlyEl = option.querySelector('[data-loop-unit-text-monthly]');
      const unitPriceTotalEl = option.querySelector('[data-loop-unit-price-total]');
      const servingPriceEl = option.querySelector('[data-loop-serving-price]');
      const comparePrice = option.querySelector('[data-loop-compare-price]');
      const price = option.querySelector('[data-loop-plan-price]');
      const badge = option.querySelector('[data-loop-discount-badge]');
      const benefitBadge = option.querySelector('[data-loop-benefit-badge]');
      const discountText = this.getDiscountText(allocation, variant, supplyDays);
      const labelPrefix = this.getSubscriptionLabel(option);

      if (this.supplySelectorMode) {
        if (label) label.textContent = labelPrefix;
        if (unitPriceEl) unitPriceEl.textContent = this.formatMoney(unitPrice);
        if (unitTextEl) unitTextEl.textContent = this.getUnitText(supplyDays);

        const monthlyPrice = this.getMonthlyUnitPrice(displayPrice, supplyDays);
        const fullCompare = this.getFullComparePrice(displayCompare, displayPrice, variant);
        if (unitPriceMonthlyEl) unitPriceMonthlyEl.textContent = this.formatMoney(monthlyPrice);
        if (unitCompareMonthlyEl) {
          unitCompareMonthlyEl.textContent = this.formatMoney(fullCompare);
          unitCompareMonthlyEl.classList.toggle(HIDDEN_CLASS, !fullCompare || fullCompare <= displayPrice);
        }
        if (unitTextMonthlyEl) unitTextMonthlyEl.textContent = ' / mo';
        if (unitPriceTotalEl) unitPriceTotalEl.textContent = this.formatMoney(displayPrice);
        if (servingPriceEl) servingPriceEl.textContent = this.formatMoney(this.getServingPrice(displayPrice, supplyDays));
      } else {
        const days = this.getSupplyDays(variant);
        if (label) {
          label.textContent = `${this.dataset.subscriptionLabel || 'Subscribe'} (${this.formatMoney(unitPrice)} ${days > 0 ? 'per day' : 'per delivery'})`;
        }
      }

      if (price) price.textContent = this.formatMoney(displayPrice);
      if (comparePrice) {
        comparePrice.textContent = this.formatMoney(displayCompare);
        comparePrice.classList.toggle(HIDDEN_CLASS, !displayCompare || displayCompare <= displayPrice);
      }
      if (badge) {
        badge.textContent = discountText;
        badge.classList.toggle(HIDDEN_CLASS, discountText === '');
      }
      const badgeShort = option.querySelector('[data-loop-discount-badge-short]');
      const discountTextShort = this.getDiscountTextShort(allocation, variant, supplyDays);
      if (badgeShort) {
        badgeShort.textContent = discountTextShort;
        badgeShort.classList.toggle(HIDDEN_CLASS, discountTextShort === '');
      }
      if (benefitBadge) benefitBadge.textContent = discountText;

      this.logPromoPricing('updateOption', {
        mode: this.supplySelectorMode ? 'supply-selector' : 'default',
        supplyDays,
        variantId: variant?.id,
        variantOptions: variant?.options,
        supplyDaysDetected: this.getSupplyDays(variant),
        promoPercent: this.getPromoDiscountPercent(),
        applyPromo,
        allocation: allocation
          ? {
              price: allocation.price,
              compare_at_price: allocation.compare_at_price,
              per_delivery_price: allocation.per_delivery_price,
              selling_plan_id: allocation.selling_plan_id,
            }
          : null,
        allocationPrice,
        promoBasePrice: applyPromo ? promoBasePrice : null,
        variantPrice: variant?.price,
        displayPrice,
        displayCompare,
        planAdjustmentPercent: this.getSellingPlanAdjustmentPercent(allocation),
        unitPrice,
        discountText,
        discountTextShort,
        note: applyPromo && promoBasePrice <= 0
          ? 'Promo skipped: variant/allocation price is 0/missing'
          : applyPromo && allocationPrice !== promoBasePrice
            ? `Promo base uses variant.price (${promoBasePrice}) instead of allocation.price (${allocationPrice})`
          : !applyPromo && this.getSupplyDays(variant) >= 90
            ? 'Promo not applied: supply is 90-day but promo percent is 0 or check failed'
            : null,
      });

      return true;
    }

    updateForVariant(options = {}) {
      const fromExternalVariantChange = options.fromExternalVariantChange === true;
      const skipSelectionSync = options.skipSelectionSync === true;
      this._updateDepth = (this._updateDepth || 0) + 1;

      if (this._updateDepth > MAX_UPDATE_DEPTH) {
        this._updateDepth = 0;
        return;
      }

      try {
        if (!this.variant) this.variant = this.getCurrentVariant();

        if (this.supplySelectorMode) {
          const userSelectionLocked = Date.now() < (this._userSelectionLockUntil || 0);
          const selectedOption = this.subscriptionOptions.find((option) => option.classList.contains(SELECTED_CLASS));
          const selectedDays = selectedOption ? Number(selectedOption.dataset.loopSupplyDays) : 0;
          const selectedVariant = selectedOption ? this.getVariantForOption(selectedOption) : null;

          if (selectedVariant && selectedDays && this.getSupplyDays(selectedVariant) === selectedDays) {
            this.variant = selectedVariant;
          }

          if (
            !skipSelectionSync &&
            !userSelectionLocked &&
            !window.__loopSupplySelectorSyncing
          ) {
            const currentDays = this.getSupplyDays(this.variant);
            if (!selectedDays || currentDays === selectedDays) {
              const matchingOption = this.subscriptionOptions.find(
                (option) => Number(option.dataset.loopSupplyDays) === currentDays && !option.hidden
              );
              if (matchingOption && !matchingOption.classList.contains(SELECTED_CLASS)) {
                this.setSelectedOption(matchingOption);
                const planInputs = Array.from(matchingOption.querySelectorAll('[data-loop-plan-input]'));
                const { planId } = this.findBestAllocation(
                  this.getVariantForOption(matchingOption),
                  planInputs,
                  Number(matchingOption.dataset.loopSupplyDays),
                );
                if (planId) this.checkPlan(matchingOption, planId, false);
              }
            }
          }

          this.subscriptionOptions.forEach((option) => {
            const variantForOption = this.getVariantForOption(option);
            this.updateOption(option, variantForOption || selectedVariant || this.variant);
          });

          this.updateFeatureThreeText(this.getSupplyDays(this.variant));
          this.scheduleButtonTextUpdate();
          return;
        }

        const subscriptionWasSelected = this.subscriptionOptions.some((option) => option.classList.contains(SELECTED_CLASS));

        const days = this.getSupplyDays(this.variant);
        const displayOneTimePrice = this.variant.price;
        const displayOneTimeCompare = this.variant.compare_at_price || 0;

        const oneTimePrice = this.querySelector('[data-loop-one-time-price]');
        const oneTimeCompare = this.querySelector('[data-loop-one-time-compare-price]');
        const oneTimeLabel = this.querySelector('[data-loop-one-time-option-label]');
        const oneTimeBadge = this.querySelector('[data-loop-one-time-discount-badge]');

        if (oneTimePrice && this.variant) {
          oneTimePrice.textContent = this.formatMoney(displayOneTimePrice);
        }
        if (oneTimeCompare && this.variant) {
          oneTimeCompare.textContent = this.formatMoney(displayOneTimeCompare);
          oneTimeCompare.classList.toggle(HIDDEN_CLASS, !displayOneTimeCompare || displayOneTimeCompare <= displayOneTimePrice);
        }
        if (oneTimeLabel && this.variant) {
          const unitPrice = days > 0 ? Math.ceil(displayOneTimePrice / days) : displayOneTimePrice;
          oneTimeLabel.textContent = `${this.dataset.oneTimeLabel || 'One-time purchase'} (${this.formatMoney(unitPrice)} ${days > 0 ? 'per day' : 'per delivery'})`;
        }
        if (oneTimeBadge) {
          oneTimeBadge.textContent = '';
          oneTimeBadge.classList.add(HIDDEN_CLASS);
        }

        let selectedSubscriptionAvailable = false;
        this.subscriptionOptions.forEach((option) => {
          const available = this.updateOption(option, this.variant);
          if (available && option.classList.contains(SELECTED_CLASS)) selectedSubscriptionAvailable = true;
        });

        if (!fromExternalVariantChange && subscriptionWasSelected && !selectedSubscriptionAvailable) {
          const availableSubscription = this.subscriptionOptions.find((option) => !option.hidden);
          if (availableSubscription) {
            this.selectOption(availableSubscription, { userInitiated: false });
            selectedSubscriptionAvailable = true;
          }
        }

        if (!selectedSubscriptionAvailable && this.oneTimeOption && !this.oneTimeOption.classList.contains(SELECTED_CLASS)) {
          this.setSelectedOption(this.oneTimeOption);
          this.uncheckPlans(false);
        }

        this.updateFeatureThreeText(this.getSupplyDays(this.variant));
        this.scheduleButtonTextUpdate();
      } finally {
        this._updateDepth -= 1;
      }
    }
  }

  if (!customElements.get('loop-subscription-widget-manual')) {
    customElements.define('loop-subscription-widget-manual', LoopSubscriptionWidgetManual);
  }

  window.__tokuEnablePromoPricingDebug = function () {
    window.__TOKU_DEBUG_PROMO_PRICING = true;
    try {
      window.localStorage.setItem('toku:debug-promo-pricing', '1');
    } catch (error) {
      // Ignore storage errors.
    }
    document.querySelectorAll('loop-subscription-widget-manual').forEach((widget) => {
      if (typeof widget.updateForVariant === 'function' && widget.dataset.initialized === 'true') {
        widget.updateForVariant();
      }
    });
    return true;
  };
  window.__tokuDisablePromoPricingDebug = function () {
    window.__TOKU_DEBUG_PROMO_PRICING = false;
    try {
      window.localStorage.removeItem('toku:debug-promo-pricing');
    } catch (error) {
      // Ignore storage errors.
    }
    return false;
  };

  if (window.localStorage?.getItem('toku:debug-promo-pricing') === '1') {
    window.__TOKU_DEBUG_PROMO_PRICING = true;
  }

  function installCartAddGuard() {
    if (window.__tokuLoopCartAddGuard) return;
    window.__tokuLoopCartAddGuard = true;

    const bodyIncludesSubscriptionIntent = (body) => {
      if (!body) return false;

      if (body instanceof FormData) {
        const plan = body.get('selling_plan');
        return plan !== null && String(plan).trim() !== '';
      }

      if (typeof body === 'string') {
        try {
          const json = JSON.parse(body);
          if (Array.isArray(json.items)) {
            return json.items.some((item) => item?.selling_plan);
          }
          return Boolean(json.selling_plan);
        } catch (error) {
          return false;
        }
      }

      return false;
    };

    const shouldApplyWidgetCartOverride = (body) => {
      if (window.__tokuOneTimeCartAdd) return false;
      return bodyIncludesSubscriptionIntent(body);
    };

    const syncActiveWidgets = (form) => {
      if (!form) return;
      document.querySelectorAll('loop-subscription-widget-manual[data-initialized="true"]').forEach((widget) => {
        if (typeof widget.ensureCartFormState === 'function') {
          widget.ensureCartFormState(form);
        }
      });
    };

    document.addEventListener(
      'theme:cart:add',
      (event) => {
        if (event.detail?.button?.closest?.('[data-one-time-purchase]')) return;
        syncActiveWidgets(event.detail?.button?.closest('form'));
      },
      true,
    );

    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-one-time-purchase]')) return;
        const button = event.target.closest('[data-add-to-cart]');
        if (!button) return;
        syncActiveWidgets(button.closest('form'));
      },
      true,
    );

    const originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url;
      const isCartAdd = url && /\/cart\/add(\.js)?(\?|$)/i.test(String(url));

      if (isCartAdd && shouldApplyWidgetCartOverride(init?.body)) {
        const widget = document.querySelector(
          'loop-subscription-widget-manual[data-supply-selector-mode="true"][data-initialized="true"]',
        );
        const payload = widget?.getSelectedCartPayload?.();

        if (payload?.variantId && init?.body) {
          if (init.body instanceof FormData) {
            const nextBody = new FormData();
            init.body.forEach((value, key) => {
              if (key === 'id' || key === 'selling_plan') return;
              nextBody.append(key, value);
            });
            nextBody.set('id', String(payload.variantId));
            if (payload.planId) nextBody.set('selling_plan', String(payload.planId));
            init = { ...init, body: nextBody };
          } else if (typeof init.body === 'string') {
            try {
              const json = JSON.parse(init.body);
              if (Array.isArray(json.items)) {
                json.items = json.items.map((item, index) => {
                  if (index !== 0) return item;
                  return {
                    ...item,
                    id: payload.variantId,
                    selling_plan: payload.planId || item.selling_plan,
                  };
                });
              } else {
                json.id = payload.variantId;
                if (payload.planId) json.selling_plan = payload.planId;
                else delete json.selling_plan;
              }
              init = { ...init, body: JSON.stringify(json) };
            } catch (error) {
              // Keep original body if it is not JSON.
            }
          }
        }
      }

      return originalFetch(input, init);
    };
  }

  installCartAddGuard();
})();
